import store from '../../store/configureStore';
import axios from "axios";
import { message } from 'antd';
import { toggleSessionExpireDialog } from "../../reducers/sessionDialogSlice";
import errorCodes from '../../libs/utilities/helpers/errorCodes';
import { toggleComponentErrorPage } from '../../reducers/componentErrorSlice';
import { Auth } from "../../libs/utilities/helpers/Auth";

export const api = axios.create({
    baseURL: process.env.REACT_APP_BASE_URL,
    headers: {
        "Access-Control-Allow-Origin": "*",
        "content-type": "application/json",
    },
});

const refreshApi = axios.create({
    baseURL: process.env.REACT_APP_BASE_URL,
    headers: {
        "Access-Control-Allow-Origin": "*",
        "content-type": "application/json",
    },
});

refreshApi.interceptors.request.use(
    (config) => {
        const token = Auth.getToken();
        const user = Auth.getUser();
        
        if (token) {
            config.headers.Authorization = token;
        }
        if (user) {
            config.headers.txn_id = user.Txnid;
            config.headers.user_id = user.UserId;
            config.headers.source = 'Galaxy';
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    
    failedQueue = [];
};

api.interceptors.request.use((config) => {
    const isLogin = config.url.includes('/login');
    const isRefresh = config.url.includes('/user/refresh');

    if (!isRefresh && !config.url.includes('/api/v')) {
        const apiVersion = isLogin ? '/api/v3.0' : '/api/v1.0';
        const endpointPath = config.url.startsWith('/') ? config.url : `/${config.url}`;
        config.url = `${apiVersion}${endpointPath}`;
    }

    return config;
}, (error) => {
    return Promise.reject(error);
});

api.interceptors.request.use(
    (config) => {
        const token = Auth.getToken();
        const user = Auth.getUser();        
        if (token) {
            config.headers.Authorization = token;
        }
        if (user) {
            config.headers.txn_id = user.Txnid;
            config.headers.user_id = user.UserId;
            config.headers.source = 'Galaxy';
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

api.interceptors.response.use(
    (response) => {
        return response;
    },
    async (error) => {
        const originalRequest = error.config;
        const apiCaller = error.config.url.split("/").slice(-1)[0];
        
        if (error.response && error.response.status === 401 && !originalRequest._retry) {
            const code = error.response.data?.statusCode;
            
            if (code === 'ERR0002' || code === 'ERR0005') {
                if (isRefreshing) {
                    return new Promise((resolve, reject) => {
                        failedQueue.push({ resolve, reject });
                    }).then(token => {
                        originalRequest.headers.Authorization = token;
                        return api(originalRequest);
                    }).catch(err => {
                        return Promise.reject(err);
                    });
                }

                originalRequest._retry = true;
                isRefreshing = true;

                try {
                    
                    const response = await refreshApi.get("/api/v3.0/user/refresh");
                    const { data } = response.data;
                    const newToken = data.accessToken;
                    
                    await Auth.setToken(`Bearer ${newToken}`);
                    
                    originalRequest.headers.Authorization = newToken;
                    
                    processQueue(null, newToken);
                    
                    return api(originalRequest);
                    
                } catch (refreshError) {
                    
                    processQueue(refreshError, null);
                    
                    store.dispatch(toggleSessionExpireDialog({ show: true }));
                    
                    return Promise.reject(refreshError);
                } finally {
                    isRefreshing = false;
                }
            }
        }

        // Handle other errors as before
        handleApiError(error, apiCaller === 'login' ? 'login' : 'default');
        return Promise.reject(error);
    }
);

export const handleApiError = (error, source) => {
    const errorCode = error.code;

    if (errorCode === "ERR_NETWORK") {
        if (source === 'login') {
            window.location.href = "./error";
        } else {
            store.dispatch(toggleComponentErrorPage({ show: true }));
        }
    }

    if (error.response) {  
        const status = error.response.status;
        const code = error.response.data?.statusCode;
        const errmessage = error.response.data?.message;
        
        if (status === 401 && (code === 'ERR0002' || code === 'ERR0005')) {
            // Token refresh logic is now handled in the response interceptor
            // This will only be called if refresh fails
            store.dispatch(toggleSessionExpireDialog({ show: true }));
        }
        else if (status === 401 && code === 'ERR0035') {
            message.error(errmessage);
        } else if (status >= 500 && status < 600) {
            if (source == "login") {
                window.location.href = "./error";
            } else {
                // message.error('Something went wrong . Please try again later.');
                //show error page for 50X errors
                store.dispatch(toggleComponentErrorPage({ show: true }));
            }
        } else if (status === 400) {
            if (errorCodes[code] !== undefined) {
                message.error(errorCodes[code]);
            }
        }
        else if(status === 403 && code === 'ERR_PERMISSION'){
            message.error(errmessage);
        }
        else if (status === 404) {
            store.dispatch(toggleComponentErrorPage({ show: true }));
        } else if (status === 408) {
            let urls = ['enterprise-gpt', 'chat-ai'];
            let currentRoute = window.location.href.split('/').slice(-1)[0];
            if (urls.includes(currentRoute)) {
                message.error('Our servers are currently experiencing high traffic. Please try after some time.');
            }
        } else {
            message.error('Something went wrong . Please try again later.');
        }
    }
    throw error;
};